import {Component, ElementRef, OnDestroy, ViewChild} from "@angular/core";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {from, Observable, of, Subscription} from "rxjs";
import {catchError, concatMap, map, take} from "rxjs/operators";
import {ExamSession} from "../../../shared/models/examSession";
import {ExamSessionService} from "src/app/shared/services/exam-session/exam-session.service";
import {PreregistrationService} from "src/app/shared/services/preregistration/preregistration.service";
import {FileDownloadHelper} from "../../../shared/helpers/file-download.helper";
import {
    ACCEPTED_EXTENSIONS,
    buildReport,
    createValidationContext,
    EXPECTED_COLUMNS,
    ImportRow,
    isAcceptedFile,
    parseFile,
    STATUS_LABELS,
    validateRow,
} from "../../../shared/helpers/preregistration-import.helper";


const VALIDATION_CHUNK_SIZE = 50;

const WARNING_MESSAGES = {
    1: "Une préinscription existe déjà avec cette adresse email",
    2: "Un utilisateur existe déjà avec cette adresse email",
    3: "Le nombre maximal de candidats inscrits à cette session est atteint",
};


@Component({
    selector: "app-preregistration-import-modal",
    templateUrl: "./preregistration-import-modal.component.html",
    styleUrls: ["./preregistration-import-modal.component.scss"]
})
export class PreregistrationImportModalComponent implements OnDestroy {

    @ViewChild("importLog") importLogElement: ElementRef<HTMLElement>;

    readonly acceptedExtensions = ACCEPTED_EXTENSIONS.join(",");
    readonly acceptedFormatsLabel = `Formats acceptés : ${ACCEPTED_EXTENSIONS.slice(0, -1).map((ext) => `*${ext}`).join(", ")} et *${ACCEPTED_EXTENSIONS[ACCEPTED_EXTENSIONS.length - 1]}`;
    readonly expectedColumns = EXPECTED_COLUMNS;
    readonly statusLabels = STATUS_LABELS;

    step: 1 | 2 | 3 = 1;
    file: File | null = null;
    fileError: string;

    // Étape 2
    analyzing: boolean = false;
    analysisProgress: number = 0;
    analysisError: string;
    rows: Array<ImportRow> = [];
    byPassWarning: boolean = false;

    // Étape 3
    importing: boolean = false;
    importDone: boolean = false;
    importedRows: Array<ImportRow> = [];
    importTotal: number = 0;
    generatingReport: boolean = false;

    private analysisId: number = 0;
    private importSubscription: Subscription;

    constructor (
        public activeModal: NgbActiveModal,
        private sessionService: ExamSessionService,
        private preregistrationService: PreregistrationService,
    ) { }

    ngOnDestroy () {
        this.analysisId++;
        if (this.importSubscription) {
            this.importSubscription.unsubscribe();
        }
    }

    get validRows (): Array<ImportRow> {
        return this.rows.filter((row) => row.status === "valid");
    }

    get importProgress (): number {
        return this.importTotal > 0 ? Math.round(this.importedRows.length / this.importTotal * 100) : 0;
    }

    countByStatus (status: string): number {
        return this.rows.filter((row) => row.status === status).length;
    }

    // ------------------------------------------------------------------
    // Étape 1 : choix du fichier
    // ------------------------------------------------------------------

    onFileChange (event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files && input.files.length > 0 ? input.files[0] : null;

        this.fileError = null;
        this.file = null;

        if (file == null) {
            return;
        }

        if (!isAcceptedFile(file)) {
            this.fileError = `Format non supporté. Formats acceptés : ${ACCEPTED_EXTENSIONS.join(", ")}`;
            input.value = "";
            return;
        }

        this.file = file;
    }

    next () {
        if (this.file == null) {
            return;
        }
        this.step = 2;
        this.analyze();
    }

    // ------------------------------------------------------------------
    // Étape 2 : analyse et aperçu
    // ------------------------------------------------------------------

    previous () {
        this.analysisId++;
        this.analyzing = false;
        this.step = 1;
    }

    private async analyze () {
        const analysisId = ++this.analysisId;
        const isCurrent = () => analysisId === this.analysisId;

        this.analyzing = true;
        this.analysisProgress = 0;
        this.analysisError = null;
        this.rows = [];

        try {
            const sessions = await this.sessionService.listPlannedOnly().pipe(take(1)).toPromise() as Array<ExamSession>;
            if (!isCurrent()) { return; }
            this.analysisProgress = 10;

            const parsed = await parseFile(this.file);
            if (!isCurrent()) { return; }
            this.analysisProgress = 30;

            // Validation par lots pour laisser la barre de progression se mettre à jour
            const context = createValidationContext(sessions);
            const rows: Array<ImportRow> = [];
            for (let start = 0; start < parsed.rows.length; start += VALIDATION_CHUNK_SIZE) {
                const end = Math.min(start + VALIDATION_CHUNK_SIZE, parsed.rows.length);
                for (let i = start; i < end; i++) {
                    rows.push(validateRow(parsed.rows[i], parsed.lines[i], context));
                }
                this.analysisProgress = 30 + Math.round(end / parsed.rows.length * 70);
                await new Promise((resolve) => setTimeout(resolve));
                if (!isCurrent()) { return; }
            }

            this.rows = rows;
        } catch (err) {
            if (!isCurrent()) { return; }
            this.analysisError = err instanceof Error && err.message
                ? err.message
                : "Une erreur est survenue lors de l'analyse du fichier";
        }

        this.analyzing = false;
    }

    // ------------------------------------------------------------------
    // Étape 3 : import
    // ------------------------------------------------------------------

    startImport () {
        const rowsToImport = this.validRows;
        if (rowsToImport.length === 0) {
            return;
        }

        this.step = 3;
        this.importing = true;
        this.importDone = false;
        this.importedRows = [];
        this.importTotal = rowsToImport.length;

        this.importSubscription = from(rowsToImport).pipe(
            concatMap((row: ImportRow) => this.importRow(row))
        ).subscribe(
            (row: ImportRow) => {
                this.importedRows.push(row);
                this.scrollImportLog();
            },
            () => {
                this.importing = false;
                this.importDone = true;
            },
            () => {
                this.importing = false;
                this.importDone = true;
            }
        );
    }

    private importRow (row: ImportRow): Observable<ImportRow> {
        return this.preregistrationService.create(
            row.firstname,
            row.lastname,
            row.email,
            row.session,
            row.phoneNumber,
            row.exam,
            row.fileNumber || null,
            this.byPassWarning,
            row.psh,
            row.pshComment || null
        ).pipe(
            map(() => {
                row.status = "imported";
                return row;
            }),
            catchError((err: any) => {
                const code = err && err.error ? err.error.code : null;
                if (code != null && WARNING_MESSAGES[code]) {
                    row.status = "warning";
                    row.messages.push(WARNING_MESSAGES[code]);
                } else {
                    row.status = "server_error";
                    row.messages.push(this.extractServerError(err));
                }
                return of(row);
            })
        );
    }

    private extractServerError (err: any): string {
        const body = err ? err.error : null;

        if (body && typeof body === "object") {
            if (body.detail) {
                return `${body.detail}`;
            }
            const details = Object.keys(body).map((key) => {
                const value = Array.isArray(body[key]) ? body[key].join(", ") : body[key];
                return `${key} : ${value}`;
            });
            if (details.length > 0) {
                return details.join(" ; ");
            }
        }

        if (typeof body === "string" && body.length > 0 && body.length < 300) {
            return body;
        }

        return "Une erreur est survenue lors de l'enregistrement de la pré-inscription";
    }

    private scrollImportLog () {
        setTimeout(() => {
            if (this.importLogElement) {
                const element = this.importLogElement.nativeElement;
                element.scrollTop = element.scrollHeight;
            }
        });
    }

    async downloadReport () {
        this.generatingReport = true;
        try {
            const blob = await buildReport(this.rows);
            FileDownloadHelper.downloadBlob(blob, `rapport-import-preinscriptions-${this.timestamp()}.xlsx`);
        } finally {
            this.generatingReport = false;
        }
    }

    private timestamp (): string {
        const now = new Date();
        const pad = (n: number) => `${n}`.padStart(2, "0");
        return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
    }

    close () {
        this.activeModal.close(this.importedRows.filter((row) => row.status === "imported").length);
    }

    dismiss () {
        if (!this.importing) {
            this.activeModal.dismiss();
        }
    }
}
